import { ComponentProps, ReactNode, useState } from 'react';
import { Text } from './Text';
import clsx from 'clsx';
import { textVariantClasses } from './Text';
import { EyeOffIcon, EyeIcon } from 'lucide-react';

interface InputProps extends ComponentProps<'input'> {
  name?: string;
  label?: string;
  error?: boolean | string;
  inputClassName?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export function Input({
  name,
  label,
  className,
  inputClassName,
  error,
  leftIcon,
  rightIcon,
  type,
  ...props
}: InputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordType = type === 'password';
  const inputType = isPasswordType && showPassword ? 'text' : type;

  return (
    <div className={className}>
      {label && (
        <Text
          as="label"
          variant="label"
          strong
          className={clsx('mb-2xs block', name && 'cursor-pointer')}
          htmlFor={name}
        >
          {label}
        </Text>
      )}
      <div
        className={clsx(
          'py-xs pl-sm pr-3xs border-transparent-dark-16 bg-base-white focus-within:border-neutral-default outline-neutral-subtle has-disabled:bg-transparent-dark-6 gap-2xs flex h-[38px] items-center rounded-md border shadow-xs transition-all duration-150 ease-in-out focus-within:outline-2 focus-within:outline-offset-2',
          error && '!border-error-default',
          textVariantClasses['label-sm-default'],
          inputClassName
        )}
      >
        {leftIcon}
        <input
          {...props}
          className={clsx(
            'placeholder:text-neutral-muted h-full w-full outline-0',
            error && 'text-error-default placeholder:!text-error-default',
            type === 'number' &&
              '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
          )}
          id={name}
          name={name}
          type={inputType}
        />
        {rightIcon}
        {isPasswordType && (
          <button
            type="button"
            className="transform cursor-pointer rounded-full"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <div className="relative">
              <EyeOffIcon
                size={20}
                className={clsx(
                  'text-neutral-muted transition-all duration-300 ease-in-out',
                  showPassword ? 'scale-100 rotate-0 opacity-100' : 'scale-75 rotate-180 opacity-0'
                )}
              />
              <EyeIcon
                size={20}
                className={clsx(
                  'text-neutral-muted absolute top-0 left-0 transition-all duration-300 ease-in-out',
                  !showPassword ? 'scale-100 rotate-0 opacity-100' : 'scale-75 rotate-180 opacity-0'
                )}
              />
            </div>
          </button>
        )}
      </div>
      {typeof error === 'string' && error.length > 0 && (
        <Text as="span" variant="label" size="xs" className="text-error-default">
          {error}
        </Text>
      )}
    </div>
  );
}
